import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { solutionKeys } from "@/lib/query";

export interface SolutionCreateBody {
  name: string;
  clientName: string;
  summary?: string | null;
  stage: "sales" | "discovery" | "build";
}

export interface Solution {
  id: string;
  name: string;
  clientName: string;
  summary: string | null;
  stage: "sales" | "discovery" | "build";
  stageLabel: string | null;
  status: "active" | "build" | "closed";
  baselineVersion: string | null;
  baselineConfirmedAt: string | null;
  startDate: string | null;
  lastActivity: string;
  leadUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useSolutions() {
  return useQuery({
    queryKey: solutionKeys.list(),
    queryFn: async (): Promise<Solution[]> => {
      const res = await fetch("/api/solutions");
      if (!res.ok) throw new Error("Failed to fetch solutions");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useSolution(solutionId: string) {
  return useQuery({
    queryKey: solutionKeys.detail(solutionId),
    queryFn: async (): Promise<Solution> => {
      const res = await fetch(`/api/solutions/${solutionId}`);
      if (!res.ok) throw new Error("Failed to fetch solution");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}

export function useCreateSolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SolutionCreateBody): Promise<Solution> => {
      const res = await fetch("/api/solutions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Failed to create solution");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: solutionKeys.list() }),
    onError: () => toast.error("Failed to create solution"),
  });
}
